import { Component, OnInit } from "@angular/core";
import { AiSettingsService } from "../../services/ai-settings.service";
import {
  AiConnection,
  AiChatSettings,
  AiChatSettingsDetail,
} from "../../models/chat.models";
import { forkJoin, of } from "rxjs";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MaterialModule } from "../../material.module";
import { LeftMenuComponent } from "../../menu/left-menu/left-menu.component";
import { SpinnerComponent } from "../../shared/spinner/spinner.component";
import { catchError } from "rxjs/operators";
import { AiChatSettingsSkeletonComponent } from "./ai-chat-settings-skeleton/ai-chat-settings-skeleton.component";

@Component({
  selector: "modernization-ai-chat-settings",
  templateUrl: "./ai-chat-settings.component.html",
  imports: [CommonModule, FormsModule, MaterialModule, LeftMenuComponent, SpinnerComponent, AiChatSettingsSkeletonComponent]
})
export class AiChatSettingsComponent implements OnInit {
  // Connections (loaded for dropdown)
  connections: AiConnection[] = [];

  // Chat Settings
  chatSettings: AiChatSettingsDetail | null = null;
  editingChatSettings: AiChatSettings | null = null;

  // UI state
  loading = false;
  saving = false;
  error: string | null = null;
  successMessage: string | null = null;

  constructor(private aiSettingsService: AiSettingsService) { }

  ngOnInit(): void {
    this.loadData();
  }

  loadData(): void {
    this.loading = true;
    this.error = null;

    forkJoin({
      connections: this.aiSettingsService.getAllConnections().pipe(
        catchError((err) => {
          console.error("Error loading connections:", err);
          this.error = "Failed to load connections";
          return of([] as AiConnection[]);
        })
      ),
      settings: this.aiSettingsService.getActiveChatSettings().pipe(
        catchError((err) => {
          if (err.status !== 404) {
            console.error("Error loading chat settings:", err);
            if (!this.error) {
              this.error = "Failed to load chat settings";
            }
          }
          return of(null);
        })
      )
    }).subscribe({
      next: ({ connections, settings }) => {
        this.connections = connections;
        this.chatSettings = settings;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  startEdit(): void {
    this.editingChatSettings = this.chatSettings
      ? {
        id: this.chatSettings.id,
        aiConnectionId: this.chatSettings.connection.id,
        isActive: true,
        maxContextWindowTokens:
          this.chatSettings.maxContextWindowTokens || 400000,
        cmdbTokenBudget: this.chatSettings.cmdbTokenBudget || 100000,
        historyTokenBudget: this.chatSettings.historyTokenBudget || 50000,
        summaryTokenBudget: this.chatSettings.summaryTokenBudget || 15000,
        systemPrompt:
          this.chatSettings.systemPrompt ||
          "You are a helpful AI assistant for IT modernization planning.",
        createdAt: this.chatSettings.createdAt,
        updatedAt: this.chatSettings.updatedAt,
      }
      : {
        id: 0,
        aiConnectionId: this.connections[0]?.id || 0,
        isActive: true,
        maxContextWindowTokens: 400000,
        cmdbTokenBudget: 100000,
        historyTokenBudget: 50000,
        summaryTokenBudget: 15000,
        systemPrompt:
          "You are a helpful AI assistant for IT modernization planning. You provide information about applications, their modernization readiness, and CMDB data. Always cite your sources.",
        createdAt: new Date(),
        updatedAt: new Date(),
      };
  }

  cancelEdit(): void {
    this.editingChatSettings = null;
  }

  save(): void {
    if (!this.editingChatSettings) return;

    if (this.editingChatSettings.aiConnectionId === 0) {
      this.error = "Please select a connection";
      return;
    }

    this.saving = true;
    this.error = null;
    this.successMessage = null;

    this.aiSettingsService.saveChatSettings(this.editingChatSettings).subscribe({
      next: () => {
        this.successMessage = "Chat settings saved successfully!";
        this.saving = false;
        this.editingChatSettings = null;
        this.loadData();
        setTimeout(() => (this.successMessage = null), 5000);
      },
      error: (err) => {
        console.error("Error saving chat settings:", err);
        this.error = err.error?.message || "Failed to save chat settings";
        this.saving = false;
      },
    });
  }
}
